


import React from 'react';
import { Clock, ArrowUpCircle, ArrowDownCircle, MapPin, Plus, X } from 'lucide-react';
import { LogicTabProps } from './types';
import { Direction, ApprovalGroupType } from '../../../types';

const LogicTab: React.FC<LogicTabProps> = ({ node, handleUpdate, availableLocations, availableApprovalGroups }) => {
    
    // Location Group handlers
    const handleLocationChange = (combinedValue: string) => {
        const [groupId, nodeId] = combinedValue.split('|');
        if (groupId && nodeId) {
            handleUpdate('locationGroup', { groupId: groupId, nodeId: nodeId });
        }
    };

    // Approval Group handlers
    const addApprovalGroup = () => {
        const defaultGroup = availableApprovalGroups[0];
        const newGroup = { 
            groupId: defaultGroup ? defaultGroup.groupId : '', 
            nodeId: defaultGroup ? defaultGroup.id : '', 
            type: ApprovalGroupType.Room 
        };
        handleUpdate('approvalGroups', [...(node.approvalGroups || []), newGroup]);
    };
    
    const removeApprovalGroup = (idx: number) => {
        const newGroups = (node.approvalGroups || []).filter((_, i) => i !== idx);
        handleUpdate('approvalGroups', newGroups);
    };

    const updateApprovalGroup = (idx: number, field: string, value: string) => {
        const newGroups = [...(node.approvalGroups || [])];
        if (field === 'combined') {
            const [groupId, nodeId] = value.split('|');
            newGroups[idx] = { ...newGroups[idx], groupId: groupId, nodeId: nodeId };
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            newGroups[idx] = { ...newGroups[idx], [field]: value } as any; 
        }
        handleUpdate('approvalGroups', newGroups);
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Priority */}
            <div>
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2 mb-4">Evaluation Logic</h3>
                <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <div>
                        <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Priority Level</div>
                        <div className="text-3xl font-mono font-bold text-slate-900">P{node.priority}</div>
                    </div>
                    <div className="flex gap-1">
                        <button 
                            onClick={() => handleUpdate('priority', Math.max(0, node.priority - 1))}
                            className="w-8 h-8 rounded bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 flex items-center justify-center transition-colors"
                        >-</button>
                        <button 
                            onClick={() => handleUpdate('priority', node.priority + 1)}
                            className="w-8 h-8 rounded bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 flex items-center justify-center transition-colors"
                        >+</button>
                    </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                    Higher priority schedules override conflicting resources from lower priority schedules (inherited or direct).
                </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Time Schedule</label>
                    <div className="relative">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <input 
                            type="text" 
                            value={node.time} 
                            onChange={(e) => handleUpdate('time', e.target.value)}
                            className="w-full pl-9 pr-3 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-semibold text-slate-500 mb-1 block">Inheritance Direction</label>
                    <div className="flex bg-slate-100 p-1 rounded-lg">
                        <button 
                            onClick={() => handleUpdate('direction', Direction.BottomUp)}
                            className={`flex-1 text-xs py-1.5 rounded-md flex items-center justify-center gap-1 transition-all ${node.direction === Direction.BottomUp ? 'bg-white text-teal-700 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <ArrowUpCircle size={12} /> Bottom Up
                        </button>
                        <button 
                            onClick={() => handleUpdate('direction', Direction.TopDown)}
                            className={`flex-1 text-xs py-1.5 rounded-md flex items-center justify-center gap-1 transition-all ${node.direction === Direction.TopDown ? 'bg-white text-teal-700 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            <ArrowDownCircle size={12} /> Top Down
                        </button>
                    </div>
                </div>
            </div>

            {/* Location */}
            <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Location Context</label>
                <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                    <select 
                        value={`${node.locationGroup.groupId}|${node.locationGroup.nodeId}`}
                        onChange={(e) => handleLocationChange(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 appearance-none"
                    >
                        <option value="">Select Location...</option>
                        {availableLocations.map(loc => (
                            <option key={loc.id} value={`${loc.groupId}|${loc.id}`}>
                                {loc.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Approval Groups */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-500 block">Approval Groups</label>
                    <button 
                        onClick={addApprovalGroup}
                        className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                    >
                        <Plus size={12} /> Add
                    </button>
                </div>
                <div className="space-y-2">
                    {(node.approvalGroups || []).map((group, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                            <select 
                                value={`${group.groupId}|${group.nodeId}`}
                                onChange={(e) => updateApprovalGroup(idx, 'combined', e.target.value)}
                                className="flex-1 p-2 text-xs bg-white text-slate-900 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500"
                            >
                                {availableApprovalGroups.map(ag => (
                                    <option key={ag.id} value={`${ag.groupId}|${ag.id}`}>
                                        {ag.name}
                                    </option>
                                ))}
                            </select>
                            <select 
                                value={group.type}
                                onChange={(e) => updateApprovalGroup(idx, 'type', e.target.value)}
                                className="w-24 p-2 text-xs bg-white text-slate-900 border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500"
                            >
                                <option value={ApprovalGroupType.Room}>Room</option>
                                <option value={ApprovalGroupType.Area}>Area</option>
                                <option value={ApprovalGroupType.Global}>Global</option>
                            </select>
                            <button 
                                onClick={() => removeApprovalGroup(idx)}
                                className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                    {(!node.approvalGroups || node.approvalGroups.length === 0) && (
                        <div className="text-center py-4 bg-slate-50 border border-dashed border-slate-200 rounded-lg text-xs text-slate-400">
                            No approval groups defined
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LogicTab;